<script lang="ts">
  import { Link } from 'svelte-routing';
  import DonorIcon from './DonorIcon.svelte';
  import FollowButton from './FollowButton.svelte';
  import { formatQuizMs } from '../lib/stats';
  import { MS_PER_DAY } from '../lib/constants';
  import type { QuizLeaderboardRow } from '../lib/leaderboards';

  // Top-N table for quiz leaderboard rows. Pure presentational —
  // owner component handles fetch + state. The calling user's row
  // (if present) is highlighted via the optional `highlightUsername`
  // prop so they can spot themselves at a glance.
  //
  // Columns: rank, player (donor icon + username), correct, total,
  // time (M:SS), last played (relative). Differs from the daily
  // table — no per-round score ribbon (quizzes don't decompose into
  // per-country scores the way daily decomposes into per-city km),
  // and the headline numbers are correct/total + time rather than
  // a single total score. Mirrors the per-mode line on the stats
  // Quiz card so a player sees consistent shapes across surfaces.

  interface Props {
    rows: QuizLeaderboardRow[];
    /** Username of the calling user — their row gets accent tint. */
    highlightUsername?: string | null;
  }

  let { rows, highlightUsername = null }: Props = $props();

  // Relative-time formatter for the "last played" column. Mirrors the
  // pattern in StatsTab (today/yesterday/N days ago/explicit date).
  function formatRelative(iso: string): string {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(iso).toLocaleDateString();
  }
</script>

<div class="leaderboard-table-wrap">
  <table class="leaderboard-table">
    <thead>
      <tr>
        <th class="leaderboard-table__rank">#</th>
        <th>Player</th>
        <th class="leaderboard-table__follow" aria-label="Follow"></th>
        <th class="leaderboard-table__num">Correct</th>
        <th class="leaderboard-table__num">Total</th>
        <th class="leaderboard-table__num leaderboard-table__total-head">Time</th>
        <th class="leaderboard-table__last-played">Last played</th>
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.username + ':' + row.rank)}
        <tr class:leaderboard-table__row--mine={row.username === highlightUsername}>
          <td class="leaderboard-table__rank">{row.rank}</td>
          <td>
            <Link
              to={`/u/${row.username}`}
              getProps={() => ({ class: 'leaderboard-table__player' })}
            >
              <DonorIcon tier={row.donor_tier} size={40} />
              <span class="leaderboard-table__name">{row.username}</span>
            </Link>
          </td>
          <td class="leaderboard-table__follow">
            <!-- Same self-suppressing FollowButton as the daily table:
                 hidden when viewer is anonymous or this is their own
                 row, so the column stays empty (but reserved-width). -->
            <FollowButton username={row.username} size="sm" compact />
          </td>
          <td class="leaderboard-table__num">{row.correct}</td>
          <td class="leaderboard-table__num leaderboard-table__avg">{row.total}</td>
          <td class="leaderboard-table__num leaderboard-table__total">{formatQuizMs(row.ms)}</td>
          <td class="leaderboard-table__last-played">{formatRelative(row.last_played)}</td>
        </tr>
      {/each}
    </tbody>
  </table>
</div>
